import { useQuery } from "@apollo/client";
import { ProfileLanguage } from "../graphql/queries/profileLanguage";
import { UserEmailVerification } from "../graphql/queries/userEmailVerification";

export interface EmailVerificationStatus {
    /**
     * True only when the backend positively reported that the current user's
     * email is not verified yet. Loading and errors yield false, so the hint
     * never shows on a hunch — a wrongly displayed "no notifications will be
     * delivered" would be worse than a missing one.
     */
    unverified: boolean;
}

/**
 * Whether the current user's email address is still unverified. Unverified
 * accounts receive no notifications at all (the backend gates every channel
 * on `user.emailVerifiedAt`), so the notification settings surface a hint.
 *
 * `Profile` does not carry the verification date; it is read from the user's
 * own record via `user(userId)`, which needs the profile id first.
 */
const useEmailVerificationStatus = (): EmailVerificationStatus => {
    const { data: profileData } = useQuery(ProfileLanguage);
    const userId: string | undefined = profileData?.profile?.id ?? undefined;

    const { data, error } = useQuery(UserEmailVerification, {
        variables: { userId },
        skip: !userId,
        fetchPolicy: "cache-first",
    });

    const user = data?.user;
    const unverified = !error && !!user && user.emailVerifiedAt === null;

    return { unverified };
};

export default useEmailVerificationStatus;
